/*
Purpose: The four concrete request inboxes, each wiring `InboxView` to its actions.
Project: TCMS

Kept together because they are variations on one list; the page container only has to
pick which one to render.
*/

import React from 'react';
import PropTypes from 'prop-types';

import BlockIcon from '@mui/icons-material/Block';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

import { DEFER_SOURCE, FORFEIT_TYPE } from '../constants';
import { buildActionKey } from '../useCalendarActions';
import InboxView from './InboxView';

/**
 * One-off deferrals (`defer_source` 2) are assigned by picking a session on the calendar.
 * Parent App deferrals (`defer_source` 1) use `/ListSlot` instead (see `onSelectSlot`).
 */
function isAssignable(record) {
  return parseInt(record.defer_source, 10) === DEFER_SOURCE.ONE_OFF;
}

function isParentAppDefer(record) {
  return parseInt(record.defer_source, 10) === DEFER_SOURCE.DEFER;
}

const listPropTypes = {
  records: PropTypes.arrayOf(PropTypes.object).isRequired,
  busyKey: PropTypes.string,
  isBusy: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
};

const listDefaultProps = {
  busyKey: null,
  isBusy: false,
};

/** Approve or reject parent-submitted defer requests. */
export function DeferRequestsInbox({
  records,
  busyKey,
  isBusy,
  onBack,
  onApprove,
  onDisapprove,
  embedded,
}) {
  return (
    <InboxView
      title="Defer requests"
      subtitle="Approve to move the lesson into the deferred bin, or disapprove to keep it on the calendar."
      icon={EventRepeatIcon}
      records={records}
      emptyMessage="No parents are waiting on a defer decision."
      onBack={onBack}
      embedded={embedded}
      renderActions={(record) => (
        <>
          <button
            type="button"
            className="cv2-btn cv2-btn--primary"
            disabled={isBusy}
            onClick={() => onApprove(record)}
          >
            {busyKey === buildActionKey(record.cancel_session_key, 'approve')
              ? 'Approving…'
              : 'Approve'}
          </button>
          <button
            type="button"
            className="cv2-btn cv2-btn--danger"
            disabled={isBusy}
            onClick={() => onDisapprove(record)}
          >
            {busyKey === buildActionKey(record.cancel_session_key, 'disapprove')
              ? 'Saving…'
              : 'Disapprove'}
          </button>
        </>
      )}
    />
  );
}

DeferRequestsInbox.propTypes = {
  ...listPropTypes,
  onApprove: PropTypes.func.isRequired,
  onDisapprove: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
};

DeferRequestsInbox.defaultProps = {
  ...listDefaultProps,
  embedded: false,
};

/** Settle approved deferrals: forfeit, write off, void, or assign to a make-up slot. */
export function DeferBinInbox({
  records,
  busyKey,
  isBusy,
  onBack,
  selectedKeys,
  onToggleSelect,
  onStartAssign,
  onSelectSlot,
  onForfeit,
  onWriteOff,
  onVoid,
  embedded,
}) {
  return (
    <InboxView
      title="Deferred bin"
      subtitle="Lessons owed back to students. Assign them to a make-up slot, or settle them as forfeit or write-off."
      icon={DeleteSweepOutlinedIcon}
      records={records}
      emptyMessage="No deferred lessons are outstanding."
      onBack={onBack}
      embedded={embedded}
      isRecordSelectable={isAssignable}
      selectedKeys={selectedKeys}
      onToggleSelect={onToggleSelect}
      renderActions={(record) => (
        <>
          {isParentAppDefer(record) ? (
            <button
              type="button"
              className="cv2-btn cv2-btn--primary"
              disabled={isBusy}
              onClick={() => onSelectSlot(record)}
            >
              Select slot
            </button>
          ) : null}
          <button
            type="button"
            className="cv2-btn cv2-btn--action-defer"
            disabled={isBusy}
            onClick={() => onForfeit(record, FORFEIT_TYPE.FORFEIT)}
          >
            {busyKey === buildActionKey(record.cancel_session_key, 'forfeit')
              ? 'Saving…'
              : 'Forfeit'}
          </button>
          <button
            type="button"
            className="cv2-btn cv2-btn--action-oneoff"
            disabled={isBusy}
            onClick={() => onWriteOff(record, FORFEIT_TYPE.WRITEOFF)}
          >
            {busyKey === buildActionKey(record.cancel_session_key, 'writeoff')
              ? 'Saving…'
              : 'Write off'}
          </button>
          <button
            type="button"
            className="cv2-btn cv2-btn--action-pause"
            disabled={isBusy}
            onClick={() => onVoid(record)}
          >
            {busyKey === buildActionKey(record.cancel_session_key, 'void') ? 'Saving…' : 'Void'}
          </button>
        </>
      )}
      footer={
        <>
          <button
            type="button"
            className="cv2-segment__btn is-active"
            disabled={selectedKeys.length === 0 || isBusy}
            onClick={onStartAssign}
          >
            Assign {selectedKeys.length || ''} selected to a session
          </button>
          <span className="cv2-inbox__subtitle">
            Pick the rows you want, then choose the target session on the day view.
          </span>
        </>
      }
    />
  );
}

DeferBinInbox.propTypes = {
  ...listPropTypes,
  selectedKeys: PropTypes.array.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onStartAssign: PropTypes.func.isRequired,
  onSelectSlot: PropTypes.func.isRequired,
  onForfeit: PropTypes.func.isRequired,
  onWriteOff: PropTypes.func.isRequired,
  onVoid: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
};

DeferBinInbox.defaultProps = {
  ...listDefaultProps,
  embedded: false,
};

/** Select one-off requests and push them onto a session. */
export function OneOffRequestsInbox({
  records,
  isBusy,
  onBack,
  selectedKeys,
  onToggleSelect,
  onStartAssign,
  embedded,
}) {
  return (
    <InboxView
      title="One-off requests"
      subtitle="Students asking for an extra session. Select the ones you want, then choose a session on the day view."
      icon={StarOutlineIcon}
      records={records}
      emptyMessage="No one-off sessions have been requested."
      onBack={onBack}
      embedded={embedded}
      isRecordSelectable={isAssignable}
      selectedKeys={selectedKeys}
      onToggleSelect={onToggleSelect}
      footer={
        <button
          type="button"
          className="cv2-btn cv2-btn--primary"
          disabled={selectedKeys.length === 0 || isBusy}
          onClick={onStartAssign}
        >
          Assign {selectedKeys.length || ''} selected to a session
        </button>
      }
    />
  );
}

OneOffRequestsInbox.propTypes = {
  ...listPropTypes,
  selectedKeys: PropTypes.array.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onStartAssign: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
};

OneOffRequestsInbox.defaultProps = {
  ...listDefaultProps,
  embedded: false,
};

/** Read-only history of rejected defer requests. */
export function DisapprovedRequestsInbox({ records, onBack, embedded }) {
  return (
    <InboxView
      title="Disapproved requests"
      subtitle="Defer requests that were rejected. These lessons stayed on the calendar."
      icon={BlockIcon}
      records={records}
      emptyMessage="No defer requests have been rejected."
      onBack={onBack}
      embedded={embedded}
    />
  );
}

DisapprovedRequestsInbox.propTypes = {
  records: PropTypes.arrayOf(PropTypes.object).isRequired,
  onBack: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
};

DisapprovedRequestsInbox.defaultProps = {
  embedded: false,
};
